import React, { type ElementRef, useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { Label } from '@atlaskit/form';
import { ErrorMessage } from '@atlaskit/form/Messages';
import { Box, xcss } from '@atlaskit/primitives';
import { AsyncSelect } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { CascadingSelectOption, CascadingSelectState } from '../common/types.tsx';
import { useCascadingSelectValuesLoader } from '../services/use-cascading-select-values-loader/useCascadingSelectValuesLoader.tsx';
import messages from './messages.tsx';
import { formatOptionLabel } from './option/index.tsx';

export type OnChange = (newValue: CascadingSelectState) => void;

type Props = {
	jqlTerm: string;
	jqlContext: string;
	initialValue?: CascadingSelectState;
	onChange: OnChange;
	setInitialFocusRef?: (ref: HTMLElement) => void;
};

const PARENT_INPUT_ID = 'jql-builder-cascading-select-parent-options-select';
const CHILD_INPUT_ID = 'jql-builder-cascading-select-child-options-select';

export const CascadingSelectView = ({
	jqlTerm,
	jqlContext,
	initialValue,
	onChange,
	setInitialFocusRef,
}: Props) => {
	const { formatMessage } = useIntl();

	// Parent data
	const [selectedParentOption, setSelectedParentOption] = useState<
		CascadingSelectOption | undefined
	>(initialValue?.parentOption);
	const { loadOptions: loadParentOptions } = useCascadingSelectValuesLoader({
		jqlTerm,
		optionType: 'PARENT',
		jqlContext,
	});

	// Child data
	const [selectedChildOption, setSelectedChildOption] = useState<CascadingSelectOption | undefined>(
		initialValue?.childOption,
	);
	const { loadOptions: loadChildOptions } = useCascadingSelectValuesLoader({
		jqlTerm,
		optionType: 'CHILD',
		jqlContext,
		parentOptionId: selectedParentOption?.value.toString() ?? undefined,
	});

	const onSelectRef = useCallback<
		(ref: ElementRef<typeof AsyncSelect<CascadingSelectOption>> | null) => void
	>(
		(ref) => {
			if (ref?.select?.inputRef && setInitialFocusRef) {
				setInitialFocusRef(ref.select.inputRef);
			}
		},
		[setInitialFocusRef],
	);

	return (
		<>
			<Box xcss={dropdownGroupContainerStyles}>
				<Box xcss={parentDropdownContainerStyles}>
					<Label htmlFor={PARENT_INPUT_ID}>{formatMessage(messages.parentSelectLabel)}</Label>
					<AsyncSelect
						ref={onSelectRef}
						inputId={PARENT_INPUT_ID}
						testId="jql-builder-basic-cascading-select.ui.select-parent"
						formatOptionLabel={formatOptionLabel}
						placeholder={formatMessage(messages.parentSelectPlaceholder)}
						noOptionsMessage={() => formatMessage(messages.noOptions)}
						value={selectedParentOption}
						loadOptions={loadParentOptions}
						onChange={(newValue) => {
							if (!newValue || newValue !== selectedParentOption) {
								setSelectedChildOption(undefined);
							}
							setSelectedParentOption(newValue ?? undefined);
						}}
						defaultOptions
						isClearable
						isInvalid={selectedParentOption?.isInvalid}
					/>
					{selectedParentOption?.isInvalid && (
						<ErrorMessage>
							{selectedParentOption?.invalidMessage ??
								formatMessage(messages.invalidOptionMessage, {
									selectField: formatMessage(messages.parentSelectLabel),
								})}
						</ErrorMessage>
					)}
				</Box>
				<Box xcss={childDropdownContainerStyles}>
					<Label htmlFor={CHILD_INPUT_ID}>{formatMessage(messages.childSelectLabel)}</Label>
					<AsyncSelect
						key={selectedParentOption?.value}
						inputId={CHILD_INPUT_ID}
						testId="jql-builder-basic-cascading-select.ui.select-child"
						formatOptionLabel={formatOptionLabel}
						placeholder={formatMessage(messages.childSelectPlaceholder)}
						noOptionsMessage={() => formatMessage(messages.noOptions)}
						value={selectedChildOption}
						loadOptions={selectedParentOption ? loadChildOptions : undefined}
						onChange={(newValue) => setSelectedChildOption(newValue ?? undefined)}
						isDisabled={!selectedParentOption}
						defaultOptions
						isClearable
						isInvalid={selectedChildOption?.isInvalid}
					/>
					{selectedChildOption?.isInvalid && !selectedParentOption?.isInvalid && (
						<ErrorMessage>
							{selectedParentOption?.invalidMessage ??
								formatMessage(messages.invalidOptionMessage, {
									selectField: formatMessage(messages.parentSelectLabel),
								})}
						</ErrorMessage>
					)}
				</Box>
			</Box>
			<StyledButtonContainer>
				<Button
					appearance="primary"
					onClick={(e) => {
						e.preventDefault();
						onChange({
							parentOption: selectedParentOption,
							childOption: selectedChildOption,
						});
					}}
					type="submit"
					isDisabled={false}
					testId="jql-builder-basic-cascading-select.ui.update-button"
				>
					{formatMessage(messages.updateButtonText)}
				</Button>
			</StyledButtonContainer>
		</>
	);
};

const dropdownGroupContainerStyles = xcss({
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
});

const parentDropdownContainerStyles = xcss({
	marginTop: 'space.100',
	marginBottom: 'space.100',
});

const childDropdownContainerStyles = xcss({
	marginTop: 'space.100',
	marginBottom: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButtonContainer = styled.div({
	borderTop: `1px solid ${token('color.border')}`,
	paddingTop: token('space.075'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.075'),
	paddingLeft: token('space.150'),
});
